import { FilterTypes } from "@madhive/mad-sdk";
import { getHandler, useCollection } from "../../../utils/collections";
import { getObservableFromData, useBehaviorSubject } from "../../rxjs";
export const getSingularDataDefaults = () => {
    // pendingChanges is initialized as a pointer to deltas because there is no data to combine with
    // deltas yet. This is useful for a creation form (no initial id).
    const deltas = {};
    return {
        deltas,
        pendingChanges: deltas,
        isLoading: false,
        isLoaded: false,
        isSaving: false,
        isInitializing: true,
        isDeleted: false
    };
};
/**
 * Utils for singular data
 * @param collection collection name
 * @param id finds a particular id
 * @returns Observer and promise/observable handler to the collection
 */
export const useSingularMadHooksUtils = (collection, id) => {
    const handler = useCollection(collection);
    const filters = id
        ? {
            where: [{ field: handler.getIdentifierKey(), type: FilterTypes.EQ, value: id }]
        }
        : undefined;
    const options = { filters, key: `${handler.getIdentifierKey()}:${id}` };
    const observer = useBehaviorSubject(collection, options, getSingularDataDefaults());
    return { observer, handler, options };
};
/**
 * Lookup singular data observable by collection and id.
 * @param collection the collection name the data belongs to
 * @param id the identifier of the data entry
 * @returns a BehaviorSubject that holds the state of the singular data entry
 */
export function getSingularDataObservable(collection, id) {
    const key = `${getHandler(collection).getIdentifierKey()}:${id}`;
    const { observable } = getObservableFromData(collection, { key }, getSingularDataDefaults());
    return observable;
}
/**
 * Creates a proxy object that combines the original data with the deltas — to make pendingChanges.
 * This allows for easy access to the current state of the data, including any unsaved changes, without mutating the original data object.
 * @param getData A getter function that returns the current data object (called on each access to ensure live reads)
 * @param deltas The changes that have been made but not yet saved
 * @returns A proxy object that reflects the current state of the data with pending changes applied
 * Note: This function is used in the useLoadSingleData hook to create the pendingChanges object
 */
export function makePendingChangesProxy(getData, deltas) {
    return new Proxy(deltas, {
        get(deltasObj, key) {
            return key in deltasObj ? deltasObj[key] : getData()?.[key];
        },
        has(deltasObj, key) {
            const data = getData();
            return key in deltasObj || (data != null && key in data);
        },
        ownKeys(deltasObj) {
            const data = getData();
            const dataKeys = data ? Reflect.ownKeys(data) : [];
            return [...new Set([...dataKeys, ...Reflect.ownKeys(deltasObj)])];
        },
        getOwnPropertyDescriptor(deltasObj, key) {
            if (key in deltasObj) {
                return Object.getOwnPropertyDescriptor(deltasObj, key);
            }
            const data = getData();
            if (data && key in data) {
                return {
                    configurable: true,
                    enumerable: true,
                    writable: true,
                    value: data[key]
                };
            }
            return undefined;
        }
    });
}
//# sourceMappingURL=singularUtils.js.map